import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, CardBody, Input, Button, Label, Spinner } from 'reactstrap';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/layout/Page';
import Title from '../../component/layout/Title';
import { toast } from 'react-toastify';
import axios from 'axios';
import { mainurl } from '../../MainUrl';
import { notification } from '../../component/hocs/notification';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const RawStockOut = () => {
  const [formData, setFormData] = useState({
    raw_material_name: '',
    stock_out: '',
    uom_id: '',
    machine_id: '',
    transaction_date: new Date().toISOString().split('T')[0],
  });

  const [uomOptions] = useState([
    { id: 1, name: 'Kilogram' },
    { id: 2, name: 'Gram' },
    { id: 3, name: 'Piece' },
    { id: 4, name: 'Liter' },
    { id: 5, name: 'Milliliter' },
  ]);
  

  const [machineList, setMachineList] = useState([]);
  const [rawMaterialList, setRawMaterialList] = useState([]); // New state for raw material list
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch data on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMachineList();
    fetchRawMaterialList(); // Fetch raw material list
    setFormData(prev => ({ ...prev, transaction_date: new Date().toISOString().split('T')[0] }));
  }, []);

  // Fetch Machine List
  const fetchMachineList = async () => {
    try {
      const response = await axios.get(mainurl + 'machine_list');
      if (Array.isArray(response.data.data)) {
        setMachineList(response.data.data);
      } else {
        console.error('Invalid data structure received for machines');
      }
    } catch (err) {
      console.error('Error fetching machine list:', err);
      notification({ type: 'error', message: 'Error while fetching machine list', position: 'bottom' });
    }
  };

  // Fetch Raw Material List
  const fetchRawMaterialList = async () => {
    try {
      const response = await axios.get(mainurl + 'raw_list');
      if (Array.isArray(response.data.data)) {
        setRawMaterialList(response.data.data);
      } else {
        console.error('Invalid data structure received for raw materials');
      }
    } catch (err) {
      console.error('Error fetching raw material list:', err);
      notification({ type: 'error', message: 'Error while fetching raw material list', position: 'bottom' });
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    if (formData.stock_out === '' || isNaN(formData.stock_out) || formData.stock_out <= 0) {
      setError('Stock out must be a valid number greater than 0');
      notification({ type: 'error', message: 'Please enter a valid stock out value', position: 'bottom' });
      setLoading(false);
      return;
    }

    
    

    try {
      const response = await axios.post(`${mainurl}raw_out`, formData);
      if (response.status === 200) {
        toast.success('Raw material added successfully');
        setFormData({
          raw_material_name: '',
          stock_out: '',
          uom_id: '',
          machine_id: '',
          transaction_date: new Date().toISOString().split('T')[0],
        });
      } else {
        setError('Failed to create');
        toast.error('Failed to create');
      }
    } catch (err) {
      console.error('Error:', err.response ? err.response.data : err);
      setError('Failed to create');
      toast.error('Failed to create');
    } finally {
      setLoading(false);
    }
  };

  // Reset form fields
  const resetForm = () => {
    setFormData({
      raw_material_name: '',
      stock_out: '',
      uom_id: '',
      machine_id: '',
      transaction_date: new Date().toISOString().split('T')[0],
    });
  };

  return (
    <div>
      <Header />
      <Page>
        <Row className="mb-3">
          <Col xs={12} className="mb-3">
            <Title pageTitle="Raw Material Stock Out" />
          </Col>
          <Col xs={12}>
            <Card color="primary" outline className="mb-3">
              <CardBody>
                <Row className="my-2">
                  {/* Raw Material Name */}
                  <Col md={6}>
                    <Label>Raw Material Name</Label>
                    <Input
                      type="select"
                      value={formData.raw_material_name}
                      onChange={(e) => setFormData({ ...formData, raw_material_name: e.target.value })}
                      required
                    >
                      <option value="">Select Raw Material</option>
                      {rawMaterialList.map((material) => (
                        <option key={material.raw_material_id} value={material.raw_material_name}>
                          {material.raw_material_name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  {/* Machine No */}
                  <Col md={6}>
                    <Label>Machine No</Label>
                    <Input
                      type="select"
                      value={formData.machine_id}
                      onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
                      required
                    >
                      <option value="">Select Machine No</option>
                      {machineList.map((machine) => (
                        <option key={machine.machine_id} value={machine.machine_id}>
                          {machine.machine_no} - {machine.machine_id}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  {/* Stock Out Quantity */}
                  <Col md={6} className="mt-2">
                    <Label>Stock Out Quantity</Label>
                    <Input
                      placeholder="Enter Stock Out Quantity"
                      type="number"
                      min="1"
                      value={formData.stock_out}
                      onChange={(e) => setFormData({ ...formData, stock_out: e.target.value })}
                      required
                    />
                  </Col>

                  {/* UOM */}
                  <Col md={6} className="mt-2">
                    <Label>Unit of Measurement (UOM)</Label>
                    <Input
                      type="select"
                      value={formData.uom_id}
                      onChange={(e) => setFormData({ ...formData, uom_id: e.target.value })}
                      required
                    >
                      <option value="">Select UOM</option>
                      {uomOptions.map((uomOption) => (
                        <option key={uomOption.id} value={uomOption.id}>
                          {uomOption.name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  {/* Transaction Date */}
                  <Col md={6}>
                                        <Label>Transaction Date</Label>
                                        <DatePicker
                                          selected={formData.transaction_date ? new Date(formData.transaction_date) : null}
                                          onChange={(date) => setFormData({ ...formData, transaction_date: date })}
                                          dateFormat="dd/MM/yyyy"
                                          placeholderText="Select a date"
                                          className="form-control"
                                          required
                                        />
                                      </Col>
                                    </Row>

                <Button
                  className="mt-3"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading || ![formData.raw_material_name, formData.uom_id, formData.stock_out, formData.machine_id, formData.transaction_date].every(Boolean)}
                >
                  {loading ? <Spinner size="sm" /> : 'Save Product'}
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Page>
      <Footer />
    </div>
  );
};

export default RawStockOut;
